import { useVault } from '../session/useVault';
import { pendingText } from '../vault/writeQueue';
import { newArcHolding, newNookHolding, withItemsAdded } from './membership';
import type { Status } from '../plan/arc';
import { currentScratch, newScratchHolding } from '../workspaces/scratch';
import type { Workspace } from '../workspaces/workspace';
import type { Holder, HolderCandidate, HolderKind, Item } from './membership';

/**
 * The writes behind every add: items into a holder that exists, a new holder
 * already holding them, and scratch canvases. Each is one write per file.
 */
export function useHolderWrites() {
  const { state, commit } = useVault();

  /** A nook is read first, since the plan does not hold it. */
  const readNook = async (path: string): Promise<Holder | null> => {
    if (state.status !== 'ready') return null;
    const raw = pendingText(path) ?? (await state.vault.readFile(path));
    return raw ? { kind: 'nook', path, raw } : null;
  };

  const addItems = async (holder: HolderCandidate, items: readonly Item[]) => {
    if (state.status !== 'ready') return;
    const target: Holder | null = holder.arc
      ? { kind: 'arc', path: holder.arc.path, raw: holder.arc.raw }
      : holder.nook
        ? await readNook(holder.nook.path)
        : null;
    if (!target) return;
    const change = (t: string) => withItemsAdded({ ...target, raw: t }, items);
    const text = change(target.raw);
    if (text !== target.raw) await commit([{ path: target.path, text, change }]);
  };

  const createHolding = async (kind: HolderKind, name: string, items: readonly Item[], status?: Status) => {
    if (state.status !== 'ready') return;
    const edit =
      kind === 'arc'
        ? newArcHolding(name, items, undefined, status)
        : newNookHolding(name, items, (candidate) => state.vault.listPaths('spaces/').includes(candidate));
    if (edit) await commit([edit]);
  };

  /** A new scratch canvas holding [items], made in one write; its path. */
  const newScratch = async (items: readonly Item[] = []): Promise<string | null> => {
    if (state.status !== 'ready') return null;
    const edit = newScratchHolding(items, (candidate) => state.vault.listPaths('spaces/').includes(candidate));
    await commit([edit]);
    return edit.path;
  };

  /** Where a send to scratch lands: [items] added to the current scratch
   *  canvas, or a new one holding them when none is current. Its path. */
  const scratch = async (nooks: readonly Workspace[], items: readonly Item[] = []): Promise<string | null> => {
    if (state.status !== 'ready') return null;
    const current = currentScratch(nooks);
    if (!current) return newScratch(items);
    if (items.length > 0) {
      const target = await readNook(current.path);
      if (!target) return null;
      const change = (t: string) => withItemsAdded({ ...target, raw: t }, items);
      const text = change(target.raw);
      if (text !== target.raw) await commit([{ path: target.path, text, change }]);
    }
    return current.path;
  };

  return { addItems, createHolding, scratch, newScratch };
}
